import { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import {
  DEFAULT_CHECKOUT_UI,
  DEFAULT_PROFILE_UI,
  DEFAULT_PROFILE_INFO_ITEMS,
  DEFAULT_RULON_NAVIGATOR_UI,
  getAdminCheckoutUi,
  getAdminProfileUi,
  getAdminRulonNavigatorUi,
  saveAdminCheckoutUi,
  saveAdminProfileUi,
  saveAdminRulonNavigatorUi,
  type CheckoutUiCopy,
  type ProfileInfoItem,
  type ProfileUiCopy,
  type RulonNavOption,
  type RulonNavigatorUiCopy,
} from '../../api';
import { useHaptic } from '../../hooks/useHaptic';
import { formatAdminPanelError } from '../../lib/adminApiError';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { ConfiguratorTileHintsPanel } from './ConfiguratorTileHintsPanel';
import { ConfiguratorStepCopyPanel } from './ConfiguratorStepCopyPanel';
import { SmartHomeUiAdminPanel } from './SmartHomeUiAdminPanel';

const CHECKOUT_FIELDS: Array<{ key: keyof CheckoutUiCopy; label: string; hint?: string }> = [
  { key: 'address_placeholder', label: 'Поле адреса — подсказка' },
  { key: 'client_name_placeholder', label: 'ФИО — подсказка' },
  { key: 'client_phone_placeholder', label: 'Телефон — подсказка' },
  { key: 'submit_button', label: 'Кнопка оформления' },
  { key: 'total_label', label: 'Подпись «Итого»' },
  { key: 'retail_total_label', label: 'Розничная сумма' },
  { key: 'savings_label', label: 'Ваша выгода' },
  { key: 'price_on_request_hint', label: 'Предупреждение «цена по запросу»' },
  { key: 'error_address_short', label: 'Ошибка: короткий адрес' },
  { key: 'error_name_short', label: 'Ошибка: короткое ФИО' },
  { key: 'error_phone_short', label: 'Ошибка: короткий телефон' },
  {
    key: 'surcharge_opening_symmetric',
    label: 'Доплата в корзине: открывание к центру',
    hint: 'Строка «+ … ₽» при симметричном открывании (сейчас +800 ₽)',
  },
  {
    key: 'surcharge_opening_oneway',
    label: 'Доплата: одностороннее открывание',
    hint: 'Подпись для доплаты +400 ₽',
  },
];

const PARTNER_FIELDS: Array<{ key: keyof Omit<ProfileUiCopy, 'info_items'>; label: string; hint?: string }> = [
  { key: 'partner_banner_title', label: 'Баннер (не партнёр) — заголовок', hint: 'Плашка «Партнёрская программа 20%»' },
  { key: 'partner_banner_sub', label: 'Баннер (не партнёр) — подпись' },
  {
    key: 'partner_active_title',
    label: 'Баннер (партнёр) — заголовок',
    hint: 'Когда скидка уже активирована',
  },
  {
    key: 'partner_active_sub',
    label: 'Баннер (партнёр) — подпись',
    hint: 'Подставьте {pct} — подставится процент скидки',
  },
];

const PROFILE_ACTION_FIELDS: Array<{ key: keyof Omit<ProfileUiCopy, 'info_items'>; label: string }> = [
  { key: 'orders_label', label: 'Мои заказы — заголовок' },
  { key: 'orders_sub', label: 'Мои заказы — подпись (мелкий текст)' },
  { key: 'measurements_label', label: 'Мои замеры — заголовок' },
  { key: 'measurements_sub', label: 'Мои замеры — подпись' },
  { key: 'manager_label', label: 'Менеджер — заголовок' },
  { key: 'manager_sub', label: 'Менеджер — подпись' },
  { key: 'manager_href', label: 'Менеджер — ссылка (Telegram / URL)' },
  { key: 'admin_label', label: 'Панель управления — заголовок' },
  { key: 'admin_sub', label: 'Панель управления — подпись' },
  { key: 'admin_sub_pending', label: 'Панель управления — подпись при новых заказах' },
  { key: 'section_title', label: 'Заголовок блока «О компании»' },
];

const RULON_SECTIONS: Array<{
  key: keyof RulonNavigatorUiCopy;
  title: string;
  hintLabel: string;
}> = [
  { key: 'install_types', title: 'Тип установки', hintLabel: 'Подпись под кнопкой' },
  { key: 'series', title: 'Диаметр профиля', hintLabel: 'Подпись под кнопкой' },
  { key: 'fabric_lines', title: 'Тип полотна', hintLabel: 'Подпись под кнопкой' },
];

const PROFILE_INFO_LABELS: Record<string, string> = {
  about: 'О компании',
  warranty: 'Гарантия',
  delivery: 'Доставка',
  install: 'Монтаж',
  support: 'Поддержка',
  faq: 'FAQ',
  service: 'Сервисный центр',
};

function mergeRulonCopy(data: RulonNavigatorUiCopy): RulonNavigatorUiCopy {
  return {
    install_types: data.install_types?.length ? data.install_types : DEFAULT_RULON_NAVIGATOR_UI.install_types,
    series: data.series?.length ? data.series : DEFAULT_RULON_NAVIGATOR_UI.series,
    fabric_lines: data.fabric_lines?.length ? data.fabric_lines : DEFAULT_RULON_NAVIGATOR_UI.fabric_lines,
  };
}

export function CheckoutUiAdminPanel() {
  const haptic = useHaptic();
  const [checkout, setCheckout] = useState<CheckoutUiCopy>(DEFAULT_CHECKOUT_UI);
  const [profile, setProfile] = useState<ProfileUiCopy>({
    ...DEFAULT_PROFILE_UI,
    info_items: DEFAULT_PROFILE_INFO_ITEMS,
  });
  const [rulonNav, setRulonNav] = useState<RulonNavigatorUiCopy>(DEFAULT_RULON_NAVIGATOR_UI);
  const [loading, setLoading] = useState(true);
  const [savingCheckout, setSavingCheckout] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingRulon, setSavingRulon] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [checkoutData, profileData, rulonData] = await Promise.all([
        getAdminCheckoutUi(),
        getAdminProfileUi(),
        getAdminRulonNavigatorUi(),
      ]);
      setCheckout({ ...DEFAULT_CHECKOUT_UI, ...checkoutData });
      setProfile({
        ...DEFAULT_PROFILE_UI,
        ...profileData,
        info_items: profileData.info_items?.length ? profileData.info_items : DEFAULT_PROFILE_INFO_ITEMS,
      });
      setRulonNav(mergeRulonCopy(rulonData));
    } catch {
      setCheckout(DEFAULT_CHECKOUT_UI);
      setProfile({ ...DEFAULT_PROFILE_UI, info_items: DEFAULT_PROFILE_INFO_ITEMS });
      setRulonNav(DEFAULT_RULON_NAVIGATOR_UI);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSaveCheckout = async () => {
    setSavingCheckout(true);
    setMessage(null);
    try {
      const saved = await saveAdminCheckoutUi(checkout);
      setCheckout({ ...DEFAULT_CHECKOUT_UI, ...saved });
      haptic.success();
      setMessage('Тексты корзины сохранены.');
    } catch (e) {
      haptic.error();
      setMessage(formatAdminPanelError(e, 'Не удалось сохранить корзину.'));
    } finally {
      setSavingCheckout(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setMessage(null);
    try {
      const saved = await saveAdminProfileUi(profile);
      setProfile({
        ...DEFAULT_PROFILE_UI,
        ...saved,
        info_items: saved.info_items?.length ? saved.info_items : DEFAULT_PROFILE_INFO_ITEMS,
      });
      haptic.success();
      setMessage('Тексты профиля сохранены.');
    } catch (e) {
      haptic.error();
      setMessage(formatAdminPanelError(e, 'Не удалось сохранить профиль.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveRulon = async () => {
    setSavingRulon(true);
    setMessage(null);
    try {
      const saved = await saveAdminRulonNavigatorUi(rulonNav);
      setRulonNav(mergeRulonCopy(saved));
      haptic.success();
      setMessage('Подписи рулонных карточек сохранены.');
    } catch (e) {
      haptic.error();
      setMessage(formatAdminPanelError(e, 'Не удалось сохранить рулонные карточки.'));
    } finally {
      setSavingRulon(false);
    }
  };

  const updateInfoItem = (index: number, patch: Partial<ProfileInfoItem>) => {
    setProfile((prev) => {
      const items = [...(prev.info_items || [])];
      items[index] = { ...items[index], ...patch };
      return { ...prev, info_items: items };
    });
  };

  const updateRulonOption = (
    section: keyof RulonNavigatorUiCopy,
    index: number,
    patch: Partial<RulonNavOption>,
  ) => {
    setRulonNav((prev) => {
      const rows = [...prev[section]];
      rows[index] = { ...rows[index], ...patch };
      return { ...prev, [section]: rows };
    });
  };

  const renderFields = (
    fields: Array<{ key: keyof Omit<ProfileUiCopy, 'info_items'>; label: string; hint?: string }>,
  ) => (
    <div className="space-y-3">
      {fields.map(({ key, label, hint }) => (
        <label key={key} className="block">
          <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>
            {label}
          </span>
          {hint && (
            <span className="block text-[10px] mt-0.5 mb-1" style={{ color: 'var(--text-3)' }}>{hint}</span>
          )}
          <input
            value={profile[key]}
            onChange={(e) => setProfile((prev) => ({ ...prev, [key]: e.target.value }))}
            className="input-glass w-full rounded-xl px-3 py-2.5 text-sm mt-1"
          />
        </label>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--cyan)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-36">
      <p className="text-xs leading-relaxed rounded-xl px-3 py-2.5" style={{ color: 'var(--text-2)', background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-glass)' }}>
        После правок нажмите <strong style={{ color: 'var(--cyan-bright)' }}>Сохранить</strong> внизу экрана или внутри блока — иначе изменения не попадут в Mini App.
      </p>

      <AdminGlassDisclosure title="Профиль — партнёрская программа" accent="cyan">
        <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
          Плашка под именем на вкладке «Профиль»: приглашение активировать код или статус партнёра.
        </p>
        {renderFields(PARTNER_FIELDS)}
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Профиль — быстрые действия">
        <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
          Крупные карточки: «Мои заказы», «Замеры», «Менеджер», вход в админку. Мелкий текст — поля «подпись».
        </p>
        {renderFields(PROFILE_ACTION_FIELDS)}
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Профиль — блок «О компании»">
        <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
          Список ссылок внизу профиля.
        </p>
        <div className="space-y-4">
          {(profile.info_items || []).map((item, index) => (
            <div
              key={item.id}
              className="rounded-xl p-3 space-y-2"
              style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
            >
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--cyan)' }}>
                {PROFILE_INFO_LABELS[item.id] || item.label || item.id}
              </p>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Заголовок</span>
                <input
                  value={item.label}
                  onChange={(e) => updateInfoItem(index, { label: e.target.value })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Подпись</span>
                <input
                  value={item.sub}
                  onChange={(e) => updateInfoItem(index, { sub: e.target.value })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Ссылка</span>
                <input
                  value={item.href}
                  onChange={(e) => updateInfoItem(index, { href: e.target.value })}
                  className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                />
              </label>
            </div>
          ))}
        </div>
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Конфигуратор — подписи на всех плитках" accent="emerald">
        <ConfiguratorTileHintsPanel />
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Конфигуратор — подсказки над кнопками" accent="amber">
        <ConfiguratorStepCopyPanel />
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Вкладка «Умный дом»" accent="cyan">
        <SmartHomeUiAdminPanel />
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Рулонные — кнопки навигатора (шаг 2)" accent="violet">
        <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
          Кнопки внутри рулонных: «Открытая», «38 мм», «Классическая» — не плитки «Прямой / Люверсы».
        </p>
        <div className="space-y-5">
          {RULON_SECTIONS.map(({ key, title, hintLabel }) => (
            <div key={key}>
              <p className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--cyan)' }}>{title}</p>
              <div className="space-y-3">
                {rulonNav[key].map((opt, index) => (
                  <div
                    key={opt.id}
                    className="rounded-xl p-3 space-y-2"
                    style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
                  >
                    <p className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>{opt.id}</p>
                    <label className="block">
                      <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Название на кнопке</span>
                      <input
                        value={opt.label}
                        onChange={(e) => updateRulonOption(key, index, { label: e.target.value })}
                        className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>{hintLabel}</span>
                      <textarea
                        value={opt.hint}
                        rows={2}
                        onChange={(e) => updateRulonOption(key, index, { hint: e.target.value })}
                        className="input-glass w-full rounded-xl px-3 py-2 text-sm mt-0.5 resize-none"
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </AdminGlassDisclosure>

      <AdminGlassDisclosure title="Корзина и оформление заказа">
        <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--text-3)' }}>
          Подсказки полей, кнопки и подписи доплат внизу корзины.
        </p>
        <div className="space-y-3">
          {CHECKOUT_FIELDS.map(({ key, label, hint }) => (
            <label key={key} className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>
                {label}
              </span>
              {hint && (
                <span className="block text-[10px] mt-0.5 mb-1" style={{ color: 'var(--text-3)' }}>{hint}</span>
              )}
              <input
                value={checkout[key]}
                onChange={(e) => setCheckout((prev) => ({ ...prev, [key]: e.target.value }))}
                className="input-glass w-full rounded-xl px-3 py-2.5 text-sm mt-1"
              />
            </label>
          ))}
        </div>
      </AdminGlassDisclosure>

      {message && (
        <p className="text-xs px-1" style={{ color: message.includes('сохран') ? '#9AE6B4' : '#FC814A' }}>{message}</p>
      )}

      <div
        className="fixed left-0 right-0 z-40 px-4 pb-4 pt-3 space-y-2"
        style={{
          bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          background: 'linear-gradient(180deg, transparent 0%, rgba(10,25,47,0.92) 24%, rgba(10,25,47,0.98) 100%)',
          backdropFilter: 'blur(16px)',
        }}
      >
        <button
          type="button"
          disabled={savingProfile}
          onClick={() => { haptic.selection(); void handleSaveProfile(); }}
          className="btn-glow w-full text-white py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2"
        >
          <Save size={16} />
          {savingProfile ? 'Сохранение…' : 'Сохранить профиль'}
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={savingRulon}
            onClick={() => { haptic.selection(); void handleSaveRulon(); }}
            className="press w-full py-2.5 rounded-xl font-bold text-xs"
            style={{ background: 'rgba(214,188,250,0.12)', color: '#D6BCFA', border: '1px solid rgba(214,188,250,0.35)' }}
          >
            {savingRulon ? '…' : 'Рулонные'}
          </button>
          <button
            type="button"
            disabled={savingCheckout}
            onClick={() => { haptic.selection(); void handleSaveCheckout(); }}
            className="press w-full py-2.5 rounded-xl font-bold text-xs"
            style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--border-glass)' }}
          >
            {savingCheckout ? '…' : 'Корзина'}
          </button>
        </div>
      </div>
    </div>
  );
}
